<template>
  <Head title="Facultas" />
  <Container>
    <template #header>
      <Header :menu="$page.props.main_menu" v-if="$page.props.auth.user" />
    </template>
    <template #default>
      <div>
        <Breadcrumbs
          :breadcrumbs="[
            { name: 'Dashboard', href: route('dashboard') },
            { name: 'Fakultas', href: route('faculties.index') },
          ]"
        />

        <div class="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
          <div class="order-2 col-span-1 space-y-6 lg:order-1">
            <div class="bg-white border border-gray-200 shadow-lg rounded-xl">
              <div
                class="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-t-xl sm:px-6 sm:py-4"
              >
                <h2
                  class="flex items-center text-lg font-bold text-white sm:text-xl"
                >
                  <i class="mr-2 fa-solid fa-university sm:mr-3"></i>
                  {{ form.id ? "Edit Fakultas" : "Tambah Fakultas" }}
                </h2>
              </div>
              <div class="p-4 sm:p-6">
                <form class="space-y-4" @submit.prevent="submit">
                  <!-- Input Fields -->
                  <div>
                    <InputField
                      label="Kode Fakultas"
                      icon="pen"
                      v-model="form.kodefak"
                    />
                    <InputError :message="form.errors.kodefak" class="mt-2" />
                  </div>

                  <div>
                    <InputField
                      label="Nama Fakultas"
                      icon="pen"
                      v-model="form.name"
                    />
                    <InputError :message="form.errors.name" class="mt-2" />
                  </div>

                  <div>
                    <InputField
                      label="Laman Web"
                      icon="pen"
                      v-model="form.laman"
                    />
                    <InputError :message="form.errors.laman" class="mt-2" />
                  </div>

                  <div>
                    <InputField
                      label="Alamat"
                      icon="pen"
                      v-model="form.alamat"
                    />
                    <InputError :message="form.errors.alamat" class="mt-2" />
                  </div>

                  <div>
                    <InputField label="Email" icon="at" v-model="form.email" />
                    <InputError :message="form.errors.email" class="mt-2" />
                  </div>

                  <div>
                    <InputField
                      label="Singkatan"
                      icon="circle-info"
                      v-model="form.singkatan"
                    />
                    <InputError :message="form.errors.singkatan" class="mt-2" />
                  </div>

                  <!-- Submit Button -->
                  <div class="pt-4">
                    <PrimaryBtn
                      :disabled="form.processing"
                      class="w-full px-4 py-2.5 text-sm font-semibold text-white bg-indigo-600 border border-transparent rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      <i class="mr-2 fa-solid fa-floppy-disk"></i>
                      {{ form.processing ? "Menyimpan..." : "Simpan" }}
                    </PrimaryBtn>
                    <button
                      v-if="form.id"
                      type="button"
                      @click="resetForm"
                      class="w-full mt-2 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 border border-gray-300 rounded-lg shadow-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200"
                    >
                      <i class="mr-2 fa-solid fa-times"></i>
                      Batal Edit
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div class="order-1 col-span-1 lg:col-span-2 lg:order-2">
            <!-- Header Section -->
            <div class="mb-4 sm:mb-6">
              <div
                class="flex flex-col sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <h3 class="text-lg font-semibold text-gray-900">
                    Daftar Fakultas
                  </h3>
                  <p class="mt-1 text-sm text-gray-600">
                    Kelola fakultas dan informasi institusi
                  </p>
                </div>
                <div class="mt-2 text-sm text-gray-500 sm:mt-0">
                  Total: {{ faculties.total }} fakultas
                </div>
              </div>
            </div>

            <!-- Search Input -->
            <div class="relative mb-4">
              <div
                class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none"
              >
                <MagnifyingGlassVue class="w-4 h-4 text-gray-400" />
              </div>
              <input
                v-model="search"
                type="text"
                autocomplete="off"
                placeholder="Cari fakultas..."
                id="search"
                class="block w-full px-3 py-2.5 pl-10 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors duration-200"
              />
            </div>

            <!-- Desktop Table -->
            <div
              class="hidden overflow-hidden bg-white shadow ring-1 ring-black ring-opacity-5 md:block md:rounded-lg"
            >
              <div class="overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-300">
                  <thead class="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                      >
                        #
                      </th>
                      <th
                        scope="col"
                        class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 min-w-[120px]"
                      >
                        Kode
                      </th>
                      <th
                        scope="col"
                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 min-w-[200px]"
                      >
                        Nama Fakultas
                      </th>
                      <th
                        scope="col"
                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 min-w-[150px]"
                      >
                        Laman Web
                      </th>
                      <th
                        scope="col"
                        class="relative py-3.5 pl-3 pr-4 sm:pr-6 min-w-[100px]"
                      >
                        <span class="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-gray-200">
                    <tr
                      v-for="(faculty, index) in faculties.data"
                      :key="faculty.id"
                      class="transition-colors duration-150 hover:bg-gray-50"
                    >
                      <td
                        class="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 whitespace-nowrap sm:pl-6"
                      >
                        {{ getRowNumber(index) }}
                      </td>
                      <td
                        class="px-3 py-4 text-sm font-medium text-indigo-600 whitespace-nowrap"
                      >
                        {{ faculty.kodefak }}
                      </td>
                      <td
                        class="px-3 py-4 text-sm text-gray-900 whitespace-nowrap"
                      >
                        {{ faculty.name }}
                      </td>
                      <td class="px-3 py-4 text-sm whitespace-nowrap">
                        <a
                          v-if="faculty.laman"
                          :href="faculty.laman"
                          target="_blank"
                          class="text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {{ faculty.laman }}
                        </a>
                        <span v-else class="text-gray-400">-</span>
                      </td>
                      <td
                        class="relative py-4 pl-3 pr-4 text-sm font-medium text-right sm:pr-6"
                      >
                        <div
                          class="flex items-center justify-end space-x-1 sm:space-x-2"
                        >
                          <button
                            @click="editFaculty(faculty)"
                            class="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-600 bg-indigo-100 rounded-md hover:bg-indigo-200 hover:text-indigo-700 transition-colors duration-150 sm:px-2.5 sm:py-1.5"
                            title="Edit"
                          >
                            <EditIcons class="w-3 h-3" />
                            <span class="hidden ml-1 sm:inline">Edit</span>
                          </button>
                          <button
                            @click="deleteFaculty(faculty.id)"
                            class="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-100 rounded-md hover:bg-red-200 hover:text-red-700 transition-colors duration-150 sm:px-2.5 sm:py-1.5"
                            title="Delete"
                          >
                            <DeleteIcons class="w-3 h-3" />
                            <span class="hidden ml-1 sm:inline">Delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Mobile Cards -->
            <div class="space-y-4 md:hidden">
              <div
                v-for="(faculty, index) in faculties.data"
                :key="faculty.id"
                class="p-4 bg-white border border-gray-200 rounded-lg shadow-sm"
              >
                <div class="flex items-start justify-between">
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center space-x-2">
                      <span class="text-xs font-medium text-gray-500">
                        #{{ getRowNumber(index) }}
                      </span>
                      <span
                        class="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-600 bg-indigo-100 rounded-full"
                      >
                        {{ faculty.kodefak }}
                      </span>
                    </div>
                    <h3 class="mt-1 text-sm font-medium text-gray-900 truncate">
                      {{ faculty.name }}
                    </h3>
                    <p class="mt-1 text-xs text-gray-500">
                      <strong>Laman Web:</strong>
                      <a
                        v-if="faculty.laman"
                        :href="faculty.laman"
                        target="_blank"
                        class="text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {{ faculty.laman }}
                      </a>
                      <span v-else>-</span>
                    </p>
                  </div>
                  <div class="flex ml-2 space-x-1">
                    <button
                      @click="editFaculty(faculty)"
                      class="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-600 bg-indigo-100 rounded-md hover:bg-indigo-200 transition-colors duration-150"
                      title="Edit"
                    >
                      <EditIcons class="w-3 h-3" />
                    </button>
                    <button
                      @click="deleteFaculty(faculty.id)"
                      class="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-100 rounded-md hover:bg-red-200 transition-colors duration-150"
                      title="Delete"
                    >
                      <DeleteIcons class="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Pagination -->
            <Pagination
              :data="faculties"
              :updatedPageNumber="updatedPageNumber"
            />
          </div>
        </div>
      </div>
    </template>
  </Container>
</template>

<script setup>
import { Head, useForm, usePage, router } from "@inertiajs/vue3";
import { ref, computed, watch } from "vue";
import Swal from "sweetalert2";

import Container from "@/Layouts/Container.vue";
import Header from "@/Components/Header.vue";
import Breadcrumbs from "@/Components/Breadcrumbs.vue";
import InputField from "@/Components/InputField.vue";
import PrimaryBtn from "@/Components/PrimaryBtn.vue";
import InputError from "@/Components/InputError.vue";
import InputLabel from "@/Components/InputLabel.vue";
import Pagination from "@/Components/Pagination.vue";
import MagnifyingGlassVue from "@/Components/MagnifyingGlass.vue";
import EditIcons from "@/Components/Icons/EditIcons.vue";
import DeleteIcons from "@/Components/Icons/DeleteIcons.vue";

const props = defineProps({
  faculties: {
    type: Object,
    required: true,
  },
});

// Form untuk input fakultas
const form = useForm({
  id: null,
  kodefak: "",
  name: "",
  laman: "",
  alamat: "",
  email: "",
  singkatan: "",
});

let search = ref(usePage().props.search || "");
let pageNumber = ref(1);

let facultyUrl = computed(() => {
  let url = new URL(route("faculties.index"));
  url.searchParams.set("page", pageNumber.value);
  if (search.value) {
    url.searchParams.set("search", search.value);
  }
  return url;
});

// Calculate row numbers for pagination
const getRowNumber = (index) => {
  const currentPage = props.faculties.current_page || 1;
  const perPage = props.faculties.per_page || 10;
  return (currentPage - 1) * perPage + index + 1;
};

const updatedPageNumber = (link) => {
  if (search.value) {
    router.get(link, { search: search.value }, { preserveState: true });
  } else {
    router.get(link, {}, { preserveState: true });
  }
};

watch(
  () => search.value,
  (newValue) => {
    if (newValue) {
      router.get(
        route("faculties.index"),
        { search: newValue },
        {
          preserveState: true,
          replace: true,
        }
      );
    } else {
      router.get(
        route("faculties.index"),
        {},
        { preserveState: true, replace: true }
      );
    }
  }
);

const resetForm = () => {
  form.reset();
  form.clearErrors();
};

// Submit form untuk tambah/edit fakultas
const submit = () => {
  if (form.id) {
    // Update existing faculty
    form.put(route("faculties.update", form.id), {
      onSuccess: () => {
        Swal.fire({
          icon: "success",
          title: "Berhasil!",
          html: `
            <div class="text-left">
              <p class="mb-2"><strong>Fakultas berhasil diperbarui:</strong></p>
              <div class="bg-gray-50 p-3 rounded-lg">
                <p><strong>Kode:</strong> ${form.kodefak}</p>
                <p><strong>Nama:</strong> ${form.name}</p>
              </div>
            </div>
          `,
          showConfirmButton: true,
          customClass: {
            popup: "swal-mobile-responsive",
            title: "text-lg font-semibold",
            htmlContainer: "text-sm",
          },
        });
        resetForm();
      },
      onError: () => {
        Swal.fire({
          icon: "error",
          title: "Gagal!",
          text: "Terjadi kesalahan saat memperbarui fakultas.",
          customClass: {
            popup: "swal-mobile-responsive",
          },
        });
      },
    });
  } else {
    // Create new faculty
    form.post(route("faculties.store"), {
      onSuccess: () => {
        Swal.fire({
          icon: "success",
          title: "Berhasil!",
          html: `
            <div class="text-left">
              <p class="mb-2"><strong>Fakultas baru berhasil ditambahkan:</strong></p>
              <div class="bg-gray-50 p-3 rounded-lg">
                <p><strong>Kode:</strong> ${form.kodefak}</p>
                <p><strong>Nama:</strong> ${form.name}</p>
              </div>
            </div>
          `,
          showConfirmButton: true,
          customClass: {
            popup: "swal-mobile-responsive",
            title: "text-lg font-semibold",
            htmlContainer: "text-sm",
          },
        });
        resetForm();
      },
      onError: () => {
        Swal.fire({
          icon: "error",
          title: "Gagal!",
          text: "Terjadi kesalahan saat menambahkan fakultas.",
          customClass: {
            popup: "swal-mobile-responsive",
          },
        });
      },
    });
  }
};

// Cancel edit mode
const cancelEdit = () => {
  form.reset();
  form.clearErrors();
};

const deleteFaculty = (facultyId) => {
  // Find faculty data for confirmation dialog
  const faculty = props.faculties.data.find((f) => f.id === facultyId);

  Swal.fire({
    title: "Konfirmasi Hapus",
    html: `
      <div class="text-left">
        <p class="mb-3">Apakah Anda yakin ingin menghapus fakultas ini?</p>
        <div class="bg-red-50 border border-red-200 p-3 rounded-lg">
          <p><strong>Kode:</strong> ${faculty?.kodefak || "-"}</p>
          <p><strong>Nama:</strong> ${faculty?.name || "-"}</p>
          <p><strong>Laman:</strong> ${faculty?.laman || "-"}</p>
        </div>
        <p class="mt-3 text-sm text-gray-600">Data yang dihapus tidak dapat dikembalikan!</p>
      </div>
    `,
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#dc2626",
    cancelButtonColor: "#6b7280",
    confirmButtonText: "Ya, Hapus!",
    cancelButtonText: "Batal",
    customClass: {
      popup: "swal-mobile-responsive",
      title: "text-lg font-semibold",
      htmlContainer: "text-sm",
    },
  }).then((result) => {
    if (result.isConfirmed) {
      router.delete(route("faculties.destroy", facultyId), {
        onSuccess: () => {
          Swal.fire({
            icon: "success",
            title: "Terhapus!",
            text: "Fakultas berhasil dihapus.",
            customClass: {
              popup: "swal-mobile-responsive",
            },
          });
        },
        onError: () => {
          Swal.fire({
            icon: "error",
            title: "Gagal!",
            text: "Terjadi kesalahan saat menghapus fakultas.",
            customClass: {
              popup: "swal-mobile-responsive",
            },
          });
        },
      });
    }
  });
};

const editFaculty = (faculty) => {
  form.id = faculty.id;
  form.kodefak = faculty.kodefak;
  form.name = faculty.name;
  form.laman = faculty.laman;
  form.alamat = faculty.alamat;
  form.email = faculty.email;
  form.singkatan = faculty.singkatan;

  // Scroll to form
  document
    .querySelector("form")
    ?.scrollIntoView({ behavior: "smooth", block: "center" });
};
</script>

<style>
/* SweetAlert Mobile Responsive */
@media (max-width: 640px) {
  .swal-mobile-responsive {
    width: 90% !important;
    margin: 0 auto !important;
  }

  .swal-mobile-responsive .swal2-html-container {
    font-size: 14px !important;
    padding: 0.5rem !important;
  }

  .swal-mobile-responsive .swal2-title {
    font-size: 18px !important;
    padding: 0.5rem !important;
  }
}
</style>
